import { Button, Form, Input, Space, Switch } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import AddIcon from "../../../../../assets/icons/AddIcon";
import EditIcon from "../../../../../assets/icons/EditIcon";
import FileIcon from "../../../../../assets/icons/FileIcon";
import InfoTitle from "../../../../../components/common/InfoTitle";
import Loader from "../../../../../components/ui/Loader";
import { useUpdateEmployeeFinancialDataMutation } from "../../../../../features/emp-setting/employee/employeeApi";
import { useGetSingleUserQuery } from "../../../../../features/portal/user/userApi";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import EmployeeFileUpload from "../EmployeeFileUpload";
import BankAddModal from "./BankAddModal";
import MFSAddModal, { mobile_bank_list } from "./MFSAddModal";
import { replaceEditWithView } from "./utils";

export type FinancialModalType =
  | "BANK_ADD"
  | "BANK_EDIT"
  | "MFS_ADD"
  | "MFS_EDIT";

export default function FinancialInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { editId } = useParams();
  const { open, handleOpen, handleClose } = useModal<any, FinancialModalType>();
  const {
    isLoading: isGetLoading,
    isError: isGetEmployeeDetailsError,
    error: getEmployeeDetailsError,
    data: getEmployeeDetails,
  } = useGetSingleUserQuery(editId || "", {
    skip: !editId,
  });
  const [
    updateEmployeeFinancialData,
    { isLoading, isError, error, isSuccess },
  ] = useUpdateEmployeeFinancialDataMutation();
  useError(
    isGetEmployeeDetailsError,
    getEmployeeDetailsError,
    "Get employee data fail"
  );
  useError(isError, error, "Update financial information fail");
  const [bankInfo, setBankInfo] = useState<Record<string, string | number>[]>(
    []
  );
  const [MFSInfo, setMFSInfo] = useState<Record<string, string | number>[]>([]);
  const [bankActive, setBankActive] = useState<string>();
  const [MFSActive, setMFSActive] = useState<string>();

  const employeeDetails = getEmployeeDetails?.data;

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      // bank_name: values.bank_name?.toLowerCase() || null,
      // business_member_id: Number(editId),
      // member_id: employeeDetails?.member_id,
      // profile_id: employeeDetails?.member?.profile?.id,
      // account_no: values?.account_no || null,
      bank_info: bankInfo?.map((el) =>
        el?.id === bankActive
          ? { ...el, is_active: 1, id: undefined }
          : { ...el, is_active: 0, id: undefined }
      ),
      bkash_info: MFSInfo?.map((el) =>
        el?.id === MFSActive
          ? { ...el, is_active: 1, id: undefined }
          : { ...el, is_active: 0, id: undefined }
      ),
    };

    if (editId)
      updateEmployeeFinancialData({
        body: newData,
        id: editId,
      });
  };

  const handleAddBank = (val: Record<string, string | number>) => {
    if (bankInfo?.find((el) => el?.id === val?.id)) {
      setBankInfo((prev) =>
        prev?.map((el) => {
          if (el?.id === val?.id) {
            return val;
          }

          return el;
        })
      );

      return;
    }

    setBankInfo((prev) => [...prev, val]);
  };

  const handleAddMFS = (val: Record<string, string | number>) => {
    if (MFSInfo?.find((el) => el?.id === val?.id)) {
      setMFSInfo((prev) =>
        prev?.map((el) => {
          if (el?.id === val?.id) {
            return val;
          }

          return el;
        })
      );

      return;
    }

    setMFSInfo((prev) => [...prev, val]);
  };

  useEffect(() => {
    if (Object.keys(employeeDetails || {})) {
      form.setFieldsValue({
        tin_no: employeeDetails?.member?.profile?.tin_no,
        tin_certificate: employeeDetails?.member?.profile?.tin_certificate,
        // bank_name: employeeDetails?.member?.profile?.bank_info?.bank_name,
        // bank_account: employeeDetails?.member?.profile?.bank_info?.account_no,
        // bank_account_title:
        //   employeeDetails?.member?.profile?.bank_info?.bank_account_title,
        // branch_name: employeeDetails?.member?.profile?.bank_info?.branch_name,
        // routing_no: employeeDetails?.member?.profile?.bank_info?.routing_no,
        // account_no: employeeDetails?.bkash_info?.account_no,
      });
    }
  }, [form, employeeDetails]);

  useEffect(() => {
    const bank_active = bankInfo?.find((el) => el?.is_active) as any;
    if (bank_active) {
      setBankActive(bank_active?.id as string);
    }
  }, [bankInfo]);

  useEffect(() => {
    const mfs_active = MFSInfo?.find((el) => el?.is_active) as any;
    if (mfs_active) {
      setMFSActive(mfs_active?.id as string);
    }
  }, [MFSInfo]);

  const backLink = replaceEditWithView(location.pathname + location.search);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Financial Information updated successfully!");
      navigate(backLink);
    }
  }, [backLink, isSuccess, navigate]);

  const bankInfoData = employeeDetails?.member?.profile?.bank_info;

  useEffect(() => {
    if (bankInfoData?.length) {
      setBankInfo(
        bankInfoData?.map((el) => ({
          bank_name: el?.bank_name,
          bank_id: el?.bank_id,
          account_no: el?.account_no,
          bank_account_title: el?.bank_account_title,
          routing_no: el?.routing_no,
          branch_name: el?.branch_name,
          is_active: el?.is_active,
          id: uuidv4(),
        }))
      );
    }
  }, [bankInfoData]);

  useEffect(() => {
    if (employeeDetails?.bkash_info?.length) {
      setMFSInfo(
        employeeDetails?.bkash_info?.map((el) => ({
          account_no: el?.account_no,
          is_active: el?.is_active,
          type: el?.type,
          id: uuidv4(),
        }))
      );
    }
  }, [employeeDetails?.bkash_info]);

  if (isGetLoading) {
    return <Loader />;
  }

  return (
    <div>
      {(open?.type === "BANK_ADD" || open?.type === "BANK_EDIT") && (
        <BankAddModal
          handleAdd={handleAddBank}
          handleClose={handleClose}
          {...open}
        />
      )}
      {(open?.type === "MFS_ADD" || open?.type === "MFS_EDIT") && (
        <MFSAddModal
          handleAdd={handleAddMFS}
          handleClose={handleClose}
          {...open}
        />
      )}
      <InfoTitle className="mb-6" icon={<FileIcon />}>
        Financial Information
      </InfoTitle>
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="tin_no"
          label="TIN Number"
          className="mb-4"
          rules={[
            {
              validator: async (_: any, value: string) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (!value) {
                  return Promise.reject(new Error("Please enter TIN number"));
                }

                if (value.length < 12) {
                  return Promise.reject(
                    new Error("TIN number should be minimum 12")
                  );
                }

                if (value.length > 12) {
                  return Promise.reject(
                    new Error("TIN number should be maximum 12")
                  );
                }

                if (value.startsWith("-")) {
                  return Promise.reject(
                    new Error("TIN Number cannot be negative")
                  );
                }

                const regex = /^[A-Z0-9-]{5,20}$/;
                if (!regex.test(value)) {
                  return Promise.reject(
                    new Error("Please enter a valid TIN number")
                  );
                }
              },
            },
          ]}
        >
          <Input size="large" placeholder="Ex: 12345" />
        </Form.Item>
        <Form.Item
          name="tin_certificate"
          label="TIN Certificate Copy"
          className="mb-4"
          valuePropName="fileUrl"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please upload TIN photo",
          //   },
          // ]}
        >
          <EmployeeFileUpload
            form={form}
            accept=".png, .jpg, .jpeg"
            name="tin_certificate"
            onChange={(url) =>
              form.setFieldsValue({
                tin_certificate: url,
              })
            }
            fileSize={1}
          />
        </Form.Item>

        <h6 className="!text-zinc-700 text-xl !font-semibold leading-loose mb-2 flex items-center justify-between mt-4">
          Bank Account ({bankInfo?.length})
          <Button
            icon={<AddIcon />}
            type="primary"
            size="small"
            className="flex items-center !bg-transparent border-primary !text-primary"
            onClick={() => handleOpen({ type: "BANK_ADD" })}
          >
            {bankInfo?.length ? "More" : "Add"}
          </Button>
        </h6>

        <div className="grid grid-cols-2 gap-4">
          {bankInfo?.map((el) => (
            <div
              className="p-4 bg-white rounded-md outline outline-[0.75px] outline-offset-[-0.75px] outline-slate-100"
              key={el?.id}
            >
              <div className="flex justify-end mb-2">
                <Button
                  size="small"
                  type="text"
                  className="text-slate-400"
                  onClick={() => {
                    handleOpen({
                      type: "BANK_EDIT",
                      data: el,
                    });
                  }}
                >
                  <EditIcon />
                </Button>
              </div>
              <ul className="space-y-1 text-sm font-medium">
                <li>
                  <span className="min-w-[150px] inline-block text-slate-400">
                    Bank Name
                  </span>
                  <span className="text-slate-600">: {el?.bank_name}</span>
                </li>
                <li>
                  <span className="min-w-[150px] inline-block text-slate-400">
                    Account No.
                  </span>
                  <span className="text-slate-600">: {el?.account_no}</span>
                </li>
                <li>
                  <span className="min-w-[150px] inline-block text-slate-400">
                    Account Name
                  </span>
                  <span className="text-slate-600">
                    : {el?.bank_account_title}
                  </span>
                </li>
                <li>
                  <span className="min-w-[150px] inline-block text-slate-400">
                    Branch Name
                  </span>
                  <span className="text-slate-600">: {el?.branch_name}</span>
                </li>
                <li>
                  <span className="min-w-[150px] inline-block text-slate-400">
                    Routing No.
                  </span>
                  <span className="text-slate-600">: {el?.routing_no}</span>
                </li>
              </ul>

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Switch
                    size="small"
                    checked={el?.id === bankActive}
                    onChange={(checked) => {
                      if (checked) {
                        setBankActive(el?.id as string);
                      }
                    }}
                  />
                  <div className="text-slate-400 text-sm font-medium leading-normal">
                    Set as Default
                  </div>
                </div>
                <Button
                  size="small"
                  type="text"
                  className="!text-accent"
                  onClick={() => {
                    setBankInfo((prev) =>
                      prev.filter((item) => item?.id !== el?.id)
                    );
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        <h6 className="!text-zinc-700 text-xl !font-semibold leading-loose mb-2 flex items-center justify-between mt-4">
          MFS Account ({MFSInfo?.length})
          <Button
            icon={<AddIcon />}
            type="primary"
            size="small"
            className="flex items-center !bg-transparent border-primary !text-primary"
            onClick={() => handleOpen({ type: "MFS_ADD" })}
          >
            {MFSInfo?.length ? "More" : "Add"}
          </Button>
        </h6>
        <div className="grid grid-cols-2 gap-4">
          {MFSInfo?.map((el) => (
            <div className="p-4 bg-white rounded-md outline outline-[0.75px] outline-offset-[-0.75px] outline-slate-100">
              <div className="flex justify-end mb-2">
                <Button
                  size="small"
                  type="text"
                  className="text-slate-400"
                  onClick={() => {
                    handleOpen({
                      type: "MFS_EDIT",
                      data: el,
                    });
                  }}
                >
                  <EditIcon />
                </Button>
              </div>
              <ul className="space-y-1 text-sm font-medium">
                <li className="mb-2">
                  <img
                    className="h-20 w-20 object-contain"
                    src={
                      mobile_bank_list?.find((item) => item?.value === el?.type)
                        ?.logo
                    }
                    alt=""
                  />
                </li>
                {/* <li>
                  <span className="min-w-[150px] inline-block text-slate-400">
                    Type
                  </span>
                  <span className="text-slate-600">
                    :{" "}
                    {
                      mobile_bank_list?.find((item) => item?.value === el?.type)
                        ?.label
                    }
                  </span>
                </li> */}
                <li>
                  <span className="min-w-[150px] inline-block text-slate-400">
                    Account No.
                  </span>
                  <span className="text-slate-600">: {el?.account_no}</span>
                </li>
              </ul>

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Switch
                    size="small"
                    checked={el?.id === MFSActive}
                    onChange={(checked) => {
                      if (checked) {
                        setMFSActive(el?.id as string);
                      }
                    }}
                  />
                  <div className="text-slate-400 text-sm font-medium leading-normal">
                    Set as Default
                  </div>
                </div>
                <Button
                  size="small"
                  type="text"
                  className="!text-accent"
                  onClick={() => {
                    setMFSInfo((prev) =>
                      prev.filter((item) => item?.id !== el?.id)
                    );
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* <Form.Item
          name="bank_name"
          label="Bank Name"
          className="mb-4"
          rules={[
            {
              required: false,
              message: "Please select bank name",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={bankList}
            suffixIcon={<SelectDropdownLgIcon />}
          />
        </Form.Item>
        <Form.Item
          name="bank_account"
          label="Bank Account Number"
          className="mb-4"
          rules={
            editId
              ? [] // No validation rules when editing
              : [
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) {
                        return Promise.reject(
                          new Error("Please enter bank account number")
                        );
                      }

                      if (value?.length < 6) {
                        return Promise.reject(
                          new Error("Bank account number should be minimum 06")
                        );
                      }

                      if (value?.length > 19) {
                        return Promise.reject(
                          new Error("Bank account number should be maximum 19")
                        );
                      }

                      if (value.startsWith("-")) {
                        return Promise.reject(
                          new Error("Bank account cannot be negative")
                        );
                      }

                      if (!Number.isInteger(Number(value))) {
                        return Promise.reject(
                          new Error("Bank account number must be a number")
                        );
                      }

                      const regex = /^[A-Za-z0-9-]{6,34}$/;
                      if (!regex.test(value)) {
                        return Promise.reject(
                          new Error("Please enter a valid bank account number")
                        );
                      }
                    },
                  },
                ]
          }
        >
          <Input size="large" placeholder="Ex: 12345" />
        </Form.Item>
        <Form.Item
          name="bank_account_title"
          label="Bank Account Title"
          className="mb-4"
        >
          <Input size="large" placeholder="Ex: Title" />
        </Form.Item>
        <Form.Item name="branch_name" label="Branch Name" className="mb-4">
          <Input size="large" placeholder="Ex: Branch" />
        </Form.Item>
        <Form.Item name="routing_no" label="Routing No" className="mb-4">
          <Input size="large" placeholder="Ex: 123654" />
        </Form.Item>

        <Form.Item
          name="account_no"
          label="Spay Wallet"
          // label="bKash Number"
          className="mb-4"
          rules={[
            {
              validator: async (_: any, value: string) => {
                if (!value) {
                  return Promise.resolve();
                }
                // Accept with or without +88
                const phoneRegex = /^(?:\+?88)?(01[3-9])[0-9]{8}$/;
                if (!phoneRegex.test(value)) {
                  return Promise.reject(
                    new Error("Please enter a valid Spay wallet number")
                  );
                }
              },
            },
          ]}
        >
          <Input size="large" placeholder="Ex: 8801" />
        </Form.Item> */}

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={() => navigate(backLink)}
            size="middle"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
          >
            Update
          </Button>
        </Space>
      </Form>
    </div>
  );
}
